import { Component, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonSkeletonText,
  IonText,
} from '@ionic/angular/standalone';
import { PosCashRegister } from '@adminpages/pos-api';
import { PosStoreService } from '@adminpages/pos-state';

@Component({
  selector: 'foco-pos-cash-register-page',
  standalone: true,
  imports: [RouterLink, IonButton, IonContent, IonIcon, IonSkeletonText, IonText],
  template: `
    <ion-content class="pos-page register-content">
      <main class="register-shell">
        <header>
          <p>{{ store.selectedWorkspace().name }}</p>
          <h1>Selecciona caja</h1>
        </header>

        <section class="register-grid">
          @if (store.cashRegistersLoading()) {
            @for (item of [1, 2]; track item) {
              <article class="register-card skeleton-card">
                <span class="register-icon"><ion-skeleton-text animated></ion-skeleton-text></span>
                <ion-skeleton-text animated style="width: 68%; height: 24px"></ion-skeleton-text>
                <ion-skeleton-text animated style="width: 45%"></ion-skeleton-text>
              </article>
            }
          } @else {
            @for (cashRegister of store.cashRegisters(); track cashRegister.id) {
              <button
                type="button"
                class="register-card"
                [class.active]="cashRegister.id === store.selectedCashRegister()?.id"
                [disabled]="store.catalogLoading()"
                (click)="select(cashRegister)"
              >
                <span class="register-icon"><ion-icon name="cash-outline"></ion-icon></span>
                <strong>{{ cashRegister.name }}</strong>
                <small>{{ cashRegister.location || cashRegister.code }}</small>
                <span class="session-state" [class.open]="cashRegister.currentSession">
                  {{ cashRegister.currentSession ? 'Sesión abierta' : 'Sin sesión abierta' }}
                </span>
              </button>
            } @empty {
              <section class="empty-state">
                <ion-icon name="cash-outline"></ion-icon>
                <strong>No hay cajas activas</strong>
                <span>Activa una caja desde el panel administrativo.</span>
              </section>
            }
          }
        </section>

        @if (store.errorMessage(); as error) {
          <ion-text color="danger">{{ error }}</ion-text>
        }

        <div class="register-actions">
          @if (store.workspaces().length > 1) {
            <ion-button routerLink="/workspace" fill="outline">Cambiar workspace</ion-button>
          }
          <ion-button
            size="large"
            [disabled]="!store.selectedCashRegister() || store.catalogLoading()"
            (click)="continue()"
          >
            {{ store.catalogLoading() ? 'Preparando...' : 'Continuar' }}
          </ion-button>
        </div>
      </main>
    </ion-content>
  `,
  styles: `
    .register-content {
      --padding-bottom: calc(24px + var(--ion-safe-area-bottom, 0px));
      --padding-end: calc(24px + var(--ion-safe-area-right, 0px));
      --padding-start: calc(24px + var(--ion-safe-area-left, 0px));
      --padding-top: calc(24px + var(--ion-safe-area-top, 0px));
    }

    .register-shell {
      display: grid;
      gap: 24px;
      margin: 0 auto;
      max-width: 920px;
      min-height: calc(100dvh - 48px - var(--ion-safe-area-top, 0px) - var(--ion-safe-area-bottom, 0px));
      place-content: center;
    }

    header p {
      color: var(--pos-color-primary);
      font-weight: 800;
      margin: 0 0 8px;
      text-transform: uppercase;
    }

    header h1 {
      font-size: 48px;
      margin: 0;
    }

    .register-grid {
      display: grid;
      gap: 14px;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .register-card {
      background: var(--pos-color-surface);
      border: 2px solid transparent;
      border-radius: 8px;
      box-shadow: 0 10px 28px var(--pos-color-shadow);
      color: var(--pos-color-text);
      cursor: pointer;
      display: grid;
      gap: 8px;
      min-height: 190px;
      padding: 20px;
      text-align: left;
    }

    .register-card.active {
      border-color: var(--pos-color-primary);
    }

    .register-card:disabled {
      cursor: wait;
      opacity: 0.68;
    }

    .register-icon {
      align-items: center;
      background: var(--pos-color-input);
      border-radius: 8px;
      color: var(--pos-color-primary);
      display: inline-flex;
      font-size: 30px;
      height: 56px;
      justify-content: center;
      width: 56px;
    }

    .register-icon ion-skeleton-text {
      height: 100%;
      margin: 0;
      width: 100%;
    }

    .register-card strong {
      font-size: 22px;
    }

    .register-card small,
    .empty-state span {
      color: var(--pos-color-muted);
    }

    .session-state {
      color: var(--pos-color-muted);
      font-size: 13px;
      font-weight: 700;
    }

    .session-state.open {
      color: var(--pos-color-primary);
    }

    .empty-state {
      align-items: center;
      background: var(--pos-color-surface);
      border: 1px solid var(--pos-color-border);
      border-radius: 8px;
      display: grid;
      gap: 10px;
      grid-column: 1 / -1;
      justify-items: center;
      min-height: 190px;
      padding: 24px;
      text-align: center;
    }

    .empty-state ion-icon {
      color: var(--pos-color-primary);
      font-size: 34px;
    }

    .register-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      justify-content: flex-end;
    }

    .register-actions ion-button {
      margin: 0;
    }

    @media (max-width: 680px) {
      header h1 {
        font-size: 36px;
      }

      .register-grid {
        grid-template-columns: 1fr;
      }

      .register-actions ion-button {
        flex: 1 1 100%;
      }
    }
  `,
})
export class CashRegisterPageComponent {
  private readonly router = inject(Router);
  readonly store = inject(PosStoreService);

  async select(cashRegister: PosCashRegister): Promise<void> {
    if (this.store.catalogLoading()) {
      return;
    }

    await this.store.selectCashRegister(cashRegister);
  }

  continue(): void {
    if (this.store.selectedCashRegister()) {
      void this.router.navigateByUrl(this.store.selectedWorkspace().homeRoute);
    }
  }
}
